import { useCallback, useRef, useState } from 'react';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import { getAllDriveLabelsForManagerQuery } from '../../../api/users-api';
import { publishDriveLabel } from '../../../api/drive-labels-api';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

interface UsePublishLabelOptions {
  changeLogRoles: string[];
  onPublished: (label: Label) => void;
}

export function usePublishLabel({
  changeLogRoles,
  onPublished,
}: UsePublishLabelOptions) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [labelAwaitingChangeLog, setLabelAwaitingChangeLog] =
    useState<Label | null>(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const inFlightRef = useRef(false);

  const { mutate, isPending } = useMutation({
    mutationFn: (variables: { label: Label; changeLogMessage: string }) =>
      publishDriveLabel(
        variables.label.id ?? '',
        null,
        variables.changeLogMessage
      ),
    onSuccess: (_published, { label }) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.DriveLabels.PublishSuccess,
      });
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
        exact: true,
      });
      setChangeLogMessage('');
      onPublished(label);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.DriveLabels.PublishFailed,
      });
    },
    onSettled: () => {
      inFlightRef.current = false;
      setLabelAwaitingChangeLog(null);
    },
  });

  const start = useCallback(
    (label: Label, message: string) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      mutate({ label, changeLogMessage: message });
    },
    [mutate]
  );

  const publishLabel = useCallback(
    (label: Label) => {
      if (
        isAbleToSeeUIElement(changeLogRoles, DriveRoles.CreateDriveLabels.value)
      ) {
        setLabelAwaitingChangeLog(label);
      } else {
        start(label, '');
      }
    },
    [changeLogRoles, start]
  );

  return {
    publishLabel,
    isPending,
    changeLogDialogProps: {
      open: labelAwaitingChangeLog !== null,
      handleClose: () => setLabelAwaitingChangeLog(null),
      changeLogMessage,
      setChangeLogMessage,
      mutate: () => {
        if (labelAwaitingChangeLog)
          start(labelAwaitingChangeLog, changeLogMessage);
      },
      mutateLoading: isPending,
    },
  };
}
